import { useTranslation } from "react-i18next"
import { useLazyLoadQuery } from "react-relay"
import { CopySimple, Info } from "@phosphor-icons/react"
import { graphql } from "relay-runtime"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogOverlay, DialogPortal, DialogTitle } from "@/components/ui/dialog"
import { toast } from "@/hooks/useToast"
import { cn } from "@/lib/utils"
import { externalAssistanceInformationDialogQuery } from "@/modules/patients/agenda/__generated__/externalAssistanceInformationDialogQuery.graphql"

const chAgendaExternalAssistanceInformationQuery = graphql`
  query externalAssistanceInformationDialogQuery($id: ID!) {
    ch_externalAssistanceInfos(patientId: $id) {
      insureeNumber
      name
      organizationName
      phoneNumber
    }
  }
`

type ExternalAssistanceInformationDialogProps = {
  patientId: string
  onClose: () => void
}

export const ExternalAssistanceInformationDialog = ({
  patientId,
  onClose,
}: ExternalAssistanceInformationDialogProps) => {
  const { t } = useTranslation()

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogPortal>
        <DialogOverlay onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()} />
        <DialogContent
          variant="small"
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onClick={(e) => e.stopPropagation()}
          data-testid="billing-dialog">
          <DialogHeader>
            <DialogTitle>{t("patient.create.callReason.externalAssistance.title")}</DialogTitle>
          </DialogHeader>
          <ExternalAssistanceContent patientId={patientId} />
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}

type ExternalAssistanceContentProps = {
  patientId: string
  className?: string
}

export const ExternalAssistanceContent = ({ patientId, className }: ExternalAssistanceContentProps) => {
  const { t } = useTranslation()

  const { ch_externalAssistanceInfos: externalAssistanceInfos } =
    useLazyLoadQuery<externalAssistanceInformationDialogQuery>(chAgendaExternalAssistanceInformationQuery, {
      id: patientId,
    })

  if (!externalAssistanceInfos) return null

  const handleCopyNumber = async () => {
    await navigator.clipboard.writeText(externalAssistanceInfos.phoneNumber)
    toast({ description: t("patient.create.callReason.externalAssistance.notifyCopyNumberContent") })
  }

  return (
    <div className={cn("text-on-surface mr-3 ml-3 grid w-full gap-1 rounded-md p-4 text-sm", className)}>
      <div className="inline-flex">
        <Info size={24} className="text-on-surface-subdued" />
        <span className="mt-0.5 ml-2">
          {t("patient.create.callReason.externalAssistance.content.copyPhoneNumber")} {externalAssistanceInfos.name}
        </span>
      </div>
      <p className="ml-8 text-start"> {t("patient.create.callReason.externalAssistance.content.pasteTheNumber")}</p>
      <div className="ml-8 text-start">
        <span>
          {t("patient.create.callReason.externalAssistance.content.shareTheFollowing")} {externalAssistanceInfos.name}
        </span>
        <ul className="ml-8 list-disc">
          <li className="font-semibold">
            {t("patient.create.callReason.externalAssistance.content.healthInsuranceName")}:{" "}
            {externalAssistanceInfos.organizationName}
          </li>
          <li className="font-semibold">
            {t("patient.create.callReason.externalAssistance.content.memberNumber")}:{" "}
            {externalAssistanceInfos.insureeNumber ?? ""}
          </li>
        </ul>
      </div>
      <p className="mt-4 ml-8 text-start">{t("patient.create.callReason.externalAssistance.content.condition")}</p>
      <div className="mt-2 flex justify-end pr-6">
        <Button type="button" variant="primary" onClick={handleCopyNumber}>
          <CopySimple size={24} />
          {t("patient.create.callReason.externalAssistance.copyNumber")}
        </Button>
      </div>
    </div>
  )
}
